import { RouteFactory } from "@app/routePaths";
import pulseOgImage from "@assets/pulse-og-image.jpg";
import { URL_ORIGIN } from "@components/MetaTags/constants";
import {
  getUpdatedOgImageUrl,
  renderPageTitleV2,
} from "@components/MetaTags/helpers";
import { fetchShortUrl } from "@pages/Article/Article.server";
import { dayjsSingaporeTimezone, getEnvironment } from "@util/constant";
import {
  type GaCustomEventTrackerObject,
  getEnvironmentDomain,
  getFormattedUrlPath,
} from "@util/helpers";
import dayjs from "dayjs";

import type { FeedPulseItems } from "./types";
import { PulseArticleObject, SanitizedPulseData } from "./types";

export const getActiveArticleId = (pathname: string): string => {
  const activeArticleId = pathname.split("/");
  const lastItem = activeArticleId[activeArticleId.length - 1];
  return /^[0-9]+$/.test(lastItem) ? lastItem : "";
};

export const getGaDataObject = (
  title: string = "pulse intro",
  virtualUrl: string = "pulse/intro",
  id: string = "",
  articles?: SanitizedPulseData[],
  pagination?: string
) => {
  const gaObject: GaCustomEventTrackerObject = {
    event: "virtual_pv",
    level2: "pulse",
    contentcat: "free",
    contenttype: "listing",
    title,
    virtual_url: virtualUrl,
    pagination: pagination || "1",
  };

  if (id !== "" && articles) {
    const currentSlideData = articles.find((article) => article.id === id);
    gaObject["articleid"] = id;
    gaObject["cue_articleid"] = id;
    gaObject["author"] = currentSlideData?.authors || "(not set)";
    gaObject["pubdate"] =
      dayjs(currentSlideData?.published)
        .tz(dayjsSingaporeTimezone)
        .format("YYYY-MM-DD HH:mm:ss") || "(not set)";
    gaObject["title"] = currentSlideData?.title || "(not set)";
    gaObject["contenttype"] = "article";
    gaObject["virtual_url"] =
      currentSlideData?.slug.substring(1) || "(not set)";
  }

  return gaObject;
};

export const sanitizedPulseData = (data: FeedPulseItems[]) => {
  const santitizedData: Promise<SanitizedPulseData>[] = data
    .slice(0, 15)
    .filter((x) => x.id)
    .map(async (article): Promise<SanitizedPulseData> => {
      const environmentDomain = getEnvironmentDomain(getEnvironment());
      const slug = RouteFactory.pulseSlide(article.id);
      const longURL = `${environmentDomain}${slug}`;
      const shortUrl = article.shortener ?? (await fetchShortUrl(longURL));

      const imageParmeters = [
        { key: "w", value: "900" },
        { key: "f", value: "webp" },
      ];

      const media = article.media
        ? getFormattedUrlPath(article.media, imageParmeters)
        : "";

      return {
        ...article,
        summaries: article.summary.split("|").map((text) => text.trim()),
        shareUrl: shortUrl || longURL,
        slug: slug,
        media: media,
      };
    });

  return Promise.all(santitizedData);
};

export const getPulseGaTitle = (
  currentSlide: number,
  sortedPulseArticles: SanitizedPulseData[],
  metaTitle: string
) => {
  if (currentSlide === sortedPulseArticles.length) return "pulse outro";

  return metaTitle;
};

/**
 * Generates SEO data for the Pulse page, including title, description, slug, and image URL.
 *
 * @param pulseData - Optional sanitized Pulse data object containing metadata for the page.
 *
 * @returns An object containing the following properties:
 * - `title`: The title of the page, derived from `pulseData.title` or a default static title.
 * - `description`: The description of the page, derived from `pulseData.standfirst`,
 *   `pulseData.summaries[0]`, or a default static description.
 * - `slug`: The URL slug for the page, derived from `pulseData.slug` or a default static slug.
 * - `imageUrl`: The URL of the image, derived from `pulseData.media` or a default static image URL.
 */
export const getPulseSeoData = (pulseData?: SanitizedPulseData) => {
  const title = renderPageTitleV2({
    kind: "static",
    title: "Pulse",
  });
  const description = "Your 5 minutes daily news summary.";
  const slug = RouteFactory.pulse;
  const imageUrl = `${URL_ORIGIN}${pulseOgImage}`;

  if (!pulseData) {
    return {
      title,
      description,
      slug,
      imageUrl,
    };
  }

  const ogImage = getUpdatedOgImageUrl(pulseData?.media);

  return {
    title: pulseData?.title || title,
    description:
      pulseData?.standfirst || pulseData?.summaries?.[0] || description,
    slug: pulseData?.slug || slug,
    imageUrl: ogImage || imageUrl,
  };
};

/**
 * Sorts and slices pulse articles by prioritizing the first individual article.
 *
 * - The first article from `pulseIndividualArticles` (if present) is placed at the beginning of the result.
 * - Removes this article from `pulseArticles` to avoid duplication.
 * - Combines the prioritized article with the remaining articles.
 *
 * @param pulseArticles - The array of all pulse articles.
 * @param pulseIndividualArticles - The array of individual pulse articles, where the first one is prioritized.
 * @returns The sorted and sliced array of pulse articles.
 */
export const sortPulseArticles = ({
  pulseArticles,
  pulseIndividualArticles,
}: PulseArticleObject) => {
  const firstArticle = pulseIndividualArticles?.[0];
  const activeId = firstArticle?.id || "";

  const filteredArticles = pulseArticles.filter(({ id }) => id !== activeId);

  const sortedArticles = [
    ...(firstArticle ? [firstArticle] : []),
    ...filteredArticles,
  ];

  return sortedArticles;
};

export const getPulseSliceArticles = (
  pulseArticles: SanitizedPulseData[],
  sortedPulseArticles: SanitizedPulseData[]
): SanitizedPulseData[] => {
  const firstArticle = sortedPulseArticles?.[0]?.id;

  const articleInListOf15 = pulseArticles
    .slice(0, 15)
    .filter((article) => article.id === firstArticle);

  if (articleInListOf15.length === 0 || sortedPulseArticles.length === 21) {
    return sortedPulseArticles.slice(0, 16);
  }

  return sortedPulseArticles.slice(0, 15);
};

export const getRandomArbitrary = (min: number, max: number): number => {
  return Math.round(Math.random() * (max - min) + min);
};
